import { lazy, Suspense, useEffect, useState } from 'react';
import type { InsightsChartType, InsightsKpiKind, InsightsTrendPoint } from '@ucp/web/lib';
import { formatKpiValue, formatPeriod } from '../format';

const TrendChart = lazy(() => import('./TrendChart'));

/**
 * Accessible chart harness (PRO-0590, ADR-038 — NF-10 / DV-1). Every chart ships
 * with a text/data-table equivalent that is ALWAYS in the DOM (inside a collapsible
 * `<details>`, open by default), so screen-reader and no-JS users get the same
 * governed figures the chart shows. The Recharts canvas is `aria-hidden` and
 * lazy-loaded; if it never loads, the table is still authoritative. Nothing here is
 * encoded by colour alone.
 */
export function ChartWithTable({
  title,
  chart,
  kind,
  unit,
  trend,
  dataTableOpen = true,
}: {
  title: string;
  chart: InsightsChartType;
  kind: InsightsKpiKind;
  unit: string;
  trend: readonly InsightsTrendPoint[];
  /**
   * Default open/closed state of the data-table disclosure. The table always stays
   * in the DOM (NF-10); this only drives the initial expansion so a caller can offer
   * a "show data tables" preference. Defaults to open (the shipped behaviour).
   */
  dataTableOpen?: boolean;
}) {
  const reducedMotion = usePrefersReducedMotion();

  return (
    <figure className="m-0">
      <figcaption className="sr-only">{`${title} trend, most recent ${trend.length} periods`}</figcaption>
      <Suspense fallback={<div className="h-40 w-full" aria-hidden="true" />}>
        <TrendChart chart={chart} kind={kind} trend={trend} reducedMotion={reducedMotion} />
      </Suspense>
      <details className="mt-2" open={dataTableOpen}>
        <summary className="cursor-pointer text-xs font-medium text-text-secondary">
          Data table
        </summary>
        <table className="mt-2 w-full border-collapse text-sm">
          <caption className="sr-only">{`${title} values by period (${unit})`}</caption>
          <thead>
            <tr>
              <th scope="col" className="border-b border-border-default py-1 text-left font-medium text-text-secondary">
                Period
              </th>
              <th scope="col" className="border-b border-border-default py-1 text-right font-medium text-text-secondary">
                {`Value (${unit})`}
              </th>
            </tr>
          </thead>
          <tbody>
            {trend.map((point) => (
              <tr key={point.period}>
                <th scope="row" className="py-1 text-left font-normal text-text-primary">
                  {formatPeriod(point.period)}
                </th>
                <td className="py-1 text-right tabular-nums text-text-primary">
                  {formatKpiValue(point.value, kind)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const listener = (event: MediaQueryListEvent) => setReduced(event.matches);
    query.addEventListener('change', listener);
    return () => query.removeEventListener('change', listener);
  }, []);
  return reduced;
}
